// Derives a rich, operational payment state from popup vendor/ticket rows.
// Silver Sage rule: never show a generic "Pending" — always the specific state.

export type PopupPaymentStateKey =
  | "awaiting"
  | "processing"
  | "paid"
  | "failed"
  | "abandoned";

export interface PopupPaymentStateInfo {
  key: PopupPaymentStateKey;
  label: string;
  tooltip: string;
  /** Tailwind classes for the pill */
  className: string;
  /** Colored dot emoji */
  dot: string;
  /** Recommended next admin action */
  recommendedAction: string;
}

const ABANDON_THRESHOLD_HOURS = 24;

export interface PopupPaymentRowLike {
  payment_status: "pending" | "paid" | "canceled" | string;
  stripe_payment_id?: string | null;
  created_at: string;
  updated_at?: string | null;
  amount_due?: number;
}

export function derivePopupPaymentState(row: PopupPaymentRowLike): PopupPaymentStateInfo {
  const status = row.payment_status;
  const hasStripe = !!row.stripe_payment_id;
  const ageMs = Date.now() - new Date(row.created_at).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);

  if (status === "paid") {
    return {
      key: "paid",
      label: "Paid",
      tooltip: "Payment has been successfully received.",
      className: "bg-green-100 text-green-800",
      dot: "🟢",
      recommendedAction: "No action needed — payment complete.",
    };
  }

  if (status === "canceled") {
    if (hasStripe) {
      return {
        key: "failed",
        label: "Payment Failed",
        tooltip: "A payment attempt was made but was unsuccessful.",
        className: "bg-red-100 text-red-800",
        dot: "🔴",
        recommendedAction: "Contact the customer to retry payment.",
      };
    }
    return {
      key: "abandoned",
      label: "Checkout Abandoned",
      tooltip: "The customer began checkout but never completed payment.",
      className: "bg-gray-200 text-gray-700",
      dot: "⚫",
      recommendedAction: "Send a friendly follow-up with a fresh payment link.",
    };
  }

  // pending
  if (hasStripe) {
    return {
      key: "processing",
      label: "Payment Processing",
      tooltip: "Payment has been submitted and is currently processing.",
      className: "bg-yellow-100 text-yellow-800",
      dot: "🟡",
      recommendedAction: "Wait — Stripe usually confirms within several minutes.",
    };
  }

  if (ageHours >= ABANDON_THRESHOLD_HOURS) {
    return {
      key: "abandoned",
      label: "Checkout Abandoned",
      tooltip: "The customer began checkout but never completed payment.",
      className: "bg-gray-200 text-gray-700",
      dot: "⚫",
      recommendedAction: "Send a friendly follow-up with a fresh payment link.",
    };
  }

  return {
    key: "awaiting",
    label: "Awaiting Payment",
    tooltip: "Registration has been received. No successful payment has been recorded yet.",
    className: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    dot: "🟢",
    recommendedAction: "Send payment reminder.",
  };
}
